/**
 * Субъекты custom-зоны для стопки аватарок: и люди, и группы.
 *
 * Чистая часть — сборка и порядок; данные приносит `useZoneMembersMap`,
 * рисует `ZoneSubjectAvatars`. Вынесено отдельно, потому что порядок в стопке
 * решает, что попадёт под лимит (в узле дерева видно всего два субъекта) —
 * такое лучше держать тестируемым, а не в разметке.
 */
import { DRIVE_ACL_LEVEL, DRIVE_ACL_LEVEL_FULL_LABEL, isDriveAclLevel } from './driveAccess';
import type { DriveAclLevel } from './driveAccess';

/** ACL-запись зоны, как её отдаёт `FolderRepository.getAccessEntries`. */
export interface ZoneAccessEntry {
  group_id: number | null;
  user_id: number | null;
  level: string;
  subject_name: string;
}

/** Субъект ACL: пользователь или группа. */
export interface ZoneSubject {
  kind: 'user' | 'group';
  /** id пользователя либо группы (в своём пространстве id). */
  id: number;
  name: string;
  level: DriveAclLevel;
  /** Полная подпись уровня — как в селекторе формы: «Удаление, Добавление, Чтение». */
  levelLabel: string;
  /** Только для групп: сколько человек внутри (подсказка о «полноте» доступа). */
  memberCount?: number;
  /** Только для групп: первые участники — чтобы «кто это» читалось без открытия формы. */
  memberNames?: string[];
}

/** ACL-запись → субъект. Запись с неизвестным уровнем или без субъекта отбрасывается. */
export function toZoneSubject(entry: ZoneAccessEntry): ZoneSubject | null {
  if (!isDriveAclLevel(entry.level)) return null;
  const levelLabel = DRIVE_ACL_LEVEL_FULL_LABEL[entry.level];
  if (entry.group_id != null) {
    return {
      kind: 'group',
      id: entry.group_id,
      name: entry.subject_name,
      level: entry.level,
      levelLabel,
    };
  }
  if (entry.user_id != null) {
    return {
      kind: 'user',
      id: entry.user_id,
      name: entry.subject_name,
      level: entry.level,
      levelLabel,
    };
  }
  return null;
}

/**
 * Порядок в стопке: сначала группы, внутри — по убыванию уровня, затем по имени.
 *
 * Группы вперёд не для красоты: лимит стопки в дереве — два субъекта, и группа
 * говорит о доступе больше, чем один конкретный человек («Логисты» против
 * «Иванов»). Уровень по убыванию — чтобы под лимит попадали администраторы
 * зоны, а не читатели. Сортировка стабильная (не мутирует вход).
 */
export function sortZoneSubjects(subjects: ZoneSubject[]): ZoneSubject[] {
  return [...subjects].sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === 'group' ? -1 : 1;
    const byLevel = DRIVE_ACL_LEVEL[b.level] - DRIVE_ACL_LEVEL[a.level];
    if (byLevel !== 0) return byLevel;
    return a.name.localeCompare(b.name, 'ru');
  });
}

/**
 * Буква на чипе группы. Пропускает не-буквенные префиксы («1С-логисты» → «Л»),
 * иначе чип получился бы одинаковым у половины групп.
 */
export function groupInitial(name: string): string {
  const letter = name
    .trim()
    .split('')
    .find(char => char.toLowerCase() !== char.toUpperCase());
  return (letter ?? name.trim().charAt(0) ?? '?').toUpperCase();
}

/**
 * Подпись субъекта в тултипе: имя, состав (у групп) и уровень.
 *
 * У группы второй строкой — первые участники: иначе «Логисты» ничего не говорит
 * тому, кто не помнит состав, а ради этого пришлось бы открывать форму доступа.
 */
export function zoneSubjectTooltip(subject: ZoneSubject): string {
  const parts: string[] = [subject.kind === 'group' ? `Группа ${subject.name}` : subject.name];
  if (subject.kind === 'group' && subject.memberCount != null) {
    parts.push(`${subject.memberCount} ${pluralMembers(subject.memberCount)}`);
  }
  if (subject.levelLabel) parts.push(subject.levelLabel);

  const head = parts.join(' · ');
  const names = subject.memberNames ?? [];
  if (subject.kind !== 'group' || names.length === 0) return head;

  // «…» — показаны не все: превью ограничено лимитом запроса
  const rest = subject.memberCount != null ? subject.memberCount - names.length : 0;
  return `${head}\n${names.join(', ')}${rest > 0 ? ` и ещё ${rest}` : ''}`;
}

/** «участник / участника / участников» по числу. */
function pluralMembers(count: number): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return 'участник';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'участника';
  return 'участников';
}
